/** @jsx jsx */
import { Flex, Grid, Heading, Paragraph } from "@eworkit/iris";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { CollapsibleArea, CollapsibleButton, useCollapsible } from "@/components/collapsible";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { formatFullName } from "@/data/persons";
import { TimeReport } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";

interface TimeReportSummaryGeneralInformationProps {
  timeReport: TimeReport;
}

export const TimeReportSummaryGeneralInformation: FC<TimeReportSummaryGeneralInformationProps> = ({ timeReport }) => {
  const { t } = useTranslation("session");
  const { hasCompanyManagerPrivileges } = useContext(UserContext);

  const { formatDate } = useFormats();

  const collapsibleState = useCollapsible(false);

  return (
    <ResponsiveCard sx={{ py: ["sm", "sm", "sm", "md"] }}>
      <Grid gap="sm">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading size="h-sm">{t("TIME_REPORTS.SUMMARY.GENERAL_INFORMATION.HEADER")}</Heading>
          <CollapsibleButton {...collapsibleState} />
        </Flex>

        <CollapsibleArea {...collapsibleState}>
          <Grid>
            <Grid columns="1fr 1fr" gap="xs">
              {hasCompanyManagerPrivileges() && (
                <React.Fragment>
                  <Paragraph color="n700">{t("TIME_REPORTS.SUMMARY.GENERAL_INFORMATION.WORKER")}</Paragraph>
                  <Paragraph>{formatFullName(timeReport.worker)}</Paragraph>
                </React.Fragment>
              )}

              <Paragraph color="n700">{t("TIME_REPORTS.SUMMARY.GENERAL_INFORMATION.CLIENT")}</Paragraph>
              <Paragraph>{timeReport.client.name}</Paragraph>
            </Grid>
            <Grid columns="1fr 1fr" gap="xs">
              <Paragraph color="n700">{t("TIME_REPORTS.SUMMARY.GENERAL_INFORMATION.START_DATE")}</Paragraph>
              <Paragraph>{formatDate(timeReport.startDate)}</Paragraph>

              <Paragraph color="n700">{t("TIME_REPORTS.SUMMARY.GENERAL_INFORMATION.END_DATE")}</Paragraph>
              <Paragraph>{formatDate(timeReport.endDate)}</Paragraph>
            </Grid>
          </Grid>
        </CollapsibleArea>
      </Grid>
    </ResponsiveCard>
  );
};
